import { useEffect, useState } from "react";
import { BarChart3, Loader2, ChevronDown, ChevronRight, Download, Info } from "lucide-react";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { TeamFilter } from "@/components/TeamFilter";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const CELL = {
  in: "bg-emerald-500",
  break: "bg-sky-400",
  lunch: "bg-amber-400",
  out: "bg-slate-300 dark:bg-slate-700",
  pto: "bg-violet-400",
};
const LABEL = { in: "In", break: "Break", lunch: "Lunch", out: "Out", pto: "Vacation" };
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`;
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString([], { weekday: "short", day: "numeric" });

const Legend = () => (
  <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground" data-testid="presence-legend">
    {Object.keys(CELL).map((k) => (
      <span key={k} className="inline-flex items-center gap-1">
        <span className={`h-2.5 w-2.5 rounded-sm ${CELL[k]}`} />{LABEL[k]}
      </span>
    ))}
    <span className="inline-flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-border" />No data
    </span>
  </div>
);

const DayStrip = ({ hours, name, date, testid }) => (
  <div className="flex gap-[1px]" data-testid={testid}>
    {HOURS.map((h) => {
      const s = hours[h];
      return (
        <span key={h} title={`${name ? `${name} · ` : ""}${dayLabel(date)} ${hourLabel(h)} — ${s ? LABEL[s] : "no data"}`}
          className={`h-4 w-[3px] rounded-[1px] ${s ? CELL[s] : "border border-dashed border-border/70"}`} />
      );
    })}
  </div>
);

/** All staff × range heatmap + hourly coverage counts. Collapsible; condenses long ranges. */
export const CoverageBoard = ({ team = "", startOpen = false }) => {
  const [range, setRange] = useState("7");
  const [teamPick, setTeamPick] = useState([]);   // empty = everyone
  const [teamOptions, setTeamOptions] = useState([]);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => {
    if (startOpen) return true;
    try { return localStorage.getItem("coverage-open") === "1"; } catch { return false; }
  });
  const toggleOpen = () => setOpen((v) => {
    const nv = !v;
    try { localStorage.setItem("coverage-open", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });

  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const queryFor = () => {
    // Iter 122 — Today / Yesterday resolve server-side in America/Edmonton so
    // the day runs midnight → midnight MDT whatever the viewer's clock says.
    if (range === "today") return "preset=today";
    if (range === "yesterday") return "preset=yesterday";
    if (range === "custom") return customStart && customEnd ? `start=${customStart}&end=${customEnd}` : null;
    return `days=${range}`;
  };

  useEffect(() => {
    if (!open) return;
    const q = queryFor();
    if (!q) return; // custom range not fully picked yet
    setLoading(true);
    // Iter 147 — `team` scopes the whole payload server-side (people AND the
    // aggregate coverage rows) so a team page never mixes in company totals.
    // Iter 188 — `teamPick` is the in-card multi-select on top of that.
    const scope = teamPick.length ? teamPick.join(",") : team;
    api.get(`/inout/history?${q}${scope ? `&teams=${encodeURIComponent(scope)}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, range, customStart, customEnd, team, teamPick]);

  // Team list for the picker — every team anyone in the board belongs to.
  useEffect(() => {
    if (!open || teamOptions.length) return;
    api.get("/inout").then((r) => {
      const staff = r.data?.staff || [];
      const all = staff.flatMap((s) => (s.teams?.length ? s.teams : (s.team ? [s.team] : [])));
      setTeamOptions(Array.from(new Set(all.filter(Boolean))).sort());
    }).catch(() => setTeamOptions([]));
    // eslint-disable-next-line
  }, [open]);

  const people = data?.people || [];
  const dates = data?.dates || [];
  const dense = dates.length > 10; // condense so long ranges don't scroll
  const dayIn = (hrs) => (hrs || []).filter((s) => s === "in").length;
  const dayHasData = (hrs) => (hrs || []).some((s) => s != null);
  const maxCover = Math.max(1, ...(data?.coverage || []).flatMap((c) => c.hours.map((v) => v || 0)));
  const maxDayIn = Math.max(1, ...people.flatMap((p) => (p.days || []).map((d) => dayIn(d.hours))));
  const peopleCount = people.length || 1;
  // Total in-hours per day (summed across everyone), aligned to `dates`.
  const dayTotals = dates.map((_, i) => people.reduce((s, p) => s + dayIn(p.days?.[i]?.hours), 0));
  const maxDayTotal = Math.max(1, ...dayTotals);

  const STATUS_LABELS = [["in", "In"], ["break", "Break"], ["lunch", "Lunch"], ["pto", "PTO"], ["out", "Out"]];
  const HourBadge = ({ p }) => {
    const hbs = p.hours_by_status || {};
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid={`coverage-hourbadge-${p.user_id}`}
              className="shrink-0 cursor-help rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">{p.in_hours}h in</span>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs" data-testid={`coverage-hourtip-${p.user_id}`}>
            <div className="mb-1 font-semibold">{p.name} — hours (from Teams)</div>
            <div className="space-y-0.5">
              {STATUS_LABELS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-1.5"><span className={`inline-block h-2 w-2 rounded-full ${CELL[k]}`} />{l}</span>
                  <span className="font-mono tabular-nums">{hbs[k] || 0}h</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const exportCsv = () => {
    if (!people.length) return;
    const ds = data.dates || [];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Person", "Team", ...ds.map((d) => `${d} (in h)`), "Total In (h)", "Break (h)", "Lunch (h)", "PTO (h)"];
    const rows = people.map((p) => {
      const perDay = (p.days || []).map((d) => dayIn(d.hours));
      const hbs = p.hours_by_status || {};
      return [p.name, p.team || "", ...perDay, p.in_hours, hbs.break || 0, hbs.lunch || 0, hbs.pto || 0];
    });
    const sumStatus = (k) => people.reduce((s, p) => s + (p.hours_by_status?.[k] || 0), 0);
    const totalIn = people.reduce((s, p) => s + p.in_hours, 0);
    const n = people.length || 1;
    const totalRow = ["TOTAL", "", ...dayTotals, totalIn, sumStatus("break"), sumStatus("lunch"), sumStatus("pto")];
    const avgRow = ["AVERAGE / person", "", ...dayTotals.map((v) => (v / n).toFixed(1)),
      (totalIn / n).toFixed(1), (sumStatus("break") / n).toFixed(1), (sumStatus("lunch") / n).toFixed(1), (sumStatus("pto") / n).toFixed(1)];
    const csv = [header, ...rows, totalRow, avgRow].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coverage-timesheet_${data.from}_to_${data.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm" data-testid="coverage-board">
      <button type="button" onClick={toggleOpen} data-testid="coverage-toggle"
        className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 ${open ? "border-b border-border" : ""}`}>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Coverage</span>
        <span className="text-[11px] text-muted-foreground">
          In/out patterns per person — spot the gaps{data?.tz_abbr ? ` · all times are in ${data.tz_abbr}` : ""}
        </span>
        {!open && <span className="ml-auto text-[11px] text-muted-foreground">Show</span>}
      </button>

      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="coverage-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today" data-testid="coverage-range-today">Today</SelectItem>
                <SelectItem value="yesterday" data-testid="coverage-range-yesterday">Yesterday</SelectItem>
                <SelectItem value="7" data-testid="coverage-days-7">Last 7 days</SelectItem>
                <SelectItem value="14" data-testid="coverage-days-14">Last 14 days</SelectItem>
                <SelectItem value="30" data-testid="coverage-days-30">Last 30 days</SelectItem>
                <SelectItem value="90" data-testid="coverage-days-90">Last 90 days</SelectItem>
                <SelectItem value="custom" data-testid="coverage-range-custom">Custom range…</SelectItem>
              </SelectContent>
            </Select>
            {range === "custom" && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)}
                  data-testid="coverage-custom-start" className="rounded-md border border-border bg-background px-2 py-1 text-xs" />
                <span>to</span>
                <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => setCustomEnd(e.target.value)}
                  data-testid="coverage-custom-end" className="rounded-md border border-border bg-background px-2 py-1 text-xs" />
              </div>
            )}
            {range === "custom" && (!customStart || !customEnd) && (
              <span className="text-[11px] text-muted-foreground">Pick a start and end date.</span>
            )}
            {!team && teamOptions.length > 0 && (
              <TeamFilter teams={teamOptions} value={teamPick} onChange={setTeamPick}
                testid="coverage-team-filter" />
            )}
            <button type="button" onClick={exportCsv} disabled={!people.length}
              data-testid="coverage-export-csv"
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>

          {dense ? (
            /* Condensed: one bar per day, fills the width — no horizontal scroll. */
            <div className="px-4 py-3" data-testid="coverage-dense">
              {people.map((p) => (
                <div key={p.user_id} className="flex items-center gap-3 border-t border-border/50 py-1" data-testid={`coverage-row-${p.user_id}`}>
                  <div className="flex w-[190px] shrink-0 items-center gap-2">
                    <Avatar name={p.name} src={p.avatar_url} size="sm" fallback="mystery" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground" title={p.name}>{p.name}</span>
                    <HourBadge p={p} />
                  </div>
                  <div className="flex flex-1 gap-[2px]">
                    {p.days.map((d) => {
                      const inh = dayIn(d.hours);
                      const has = dayHasData(d.hours);
                      const cls = !has ? "border border-dashed border-border/70"
                        : inh === 0 ? "bg-slate-300 dark:bg-slate-700" : "bg-emerald-500";
                      return (
                        <span key={d.date} title={`${dayLabel(d.date)} — ${has ? `${inh}h in` : "no data"}`}
                          className={`h-5 flex-1 rounded-[1px] ${cls}`}
                          style={inh ? { opacity: 0.35 + 0.65 * (inh / maxDayIn) } : undefined} />
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="mt-2 flex items-center gap-3 border-t border-border pt-2" data-testid="coverage-totals">
                <div className="w-[190px] shrink-0 text-xs font-semibold text-foreground">Peak people in</div>
                <div className="flex flex-1 gap-[2px]">
                  {(data?.coverage || []).map((c) => {
                    const peak = Math.max(0, ...c.hours.map((v) => v || 0));
                    const has = c.hours.some((v) => v != null);
                    return (
                      <span key={c.date} title={`${dayLabel(c.date)} — ${has ? `${peak} in (peak)` : "no data"}`}
                        className={`h-5 flex-1 rounded-[1px] ${!has ? "border border-dashed border-border/70" : peak === 0 ? "bg-rose-200 dark:bg-rose-900/60" : "bg-primary"}`}
                        style={peak ? { opacity: 0.35 + 0.65 * (peak / maxCover) } : undefined} />
                    );
                  })}
                </div>
              </div>
              <div className="mt-1 flex items-center gap-3" data-testid="coverage-daytotals">
                <div className="w-[190px] shrink-0 text-xs font-semibold text-foreground">Total in-hours <span className="font-normal text-muted-foreground">(avg/person)</span></div>
                <div className="flex flex-1 gap-[2px]">
                  {dates.map((d, i) => (
                    <span key={d} title={`${dayLabel(d)} — ${dayTotals[i]}h total · avg ${(dayTotals[i] / peopleCount).toFixed(1)}h/person`}
                      className={`h-5 flex-1 rounded-[1px] ${dayTotals[i] ? "bg-emerald-600" : "border border-dashed border-border/70"}`}
                      style={dayTotals[i] ? { opacity: 0.35 + 0.65 * (dayTotals[i] / maxDayTotal) } : undefined} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Detailed: hour-by-hour strips. */
            <div className="overflow-x-auto px-4 py-3">
              <div className="min-w-max">
                <div className="mb-1 flex gap-3 pl-[190px]" data-testid="coverage-day-headers">
                  {dates.map((d) => {
                    const blank = (data?.gap_dates || []).includes(d) || (data?.untracked_dates || []).includes(d);
                    return (
                      <span key={d} className="w-[95px] text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {dayLabel(d)}
                        {blank && (
                          <span className="block font-normal normal-case text-amber-600 dark:text-amber-400"
                            data-testid={`coverage-nodata-${d}`}
                            title={(data?.untracked_dates || []).includes(d) ? "Before tracking started" : "Collector wasn't running"}>
                            no data
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
                {people.map((p) => (
                  <div key={p.user_id} className="flex items-center gap-3 border-t border-border/50 py-1" data-testid={`coverage-row-${p.user_id}`}>
                    <div className="flex w-[190px] shrink-0 items-center gap-2">
                      <Avatar name={p.name} src={p.avatar_url} size="sm" fallback="mystery" />
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground" title={p.name}>{p.name}</span>
                      <HourBadge p={p} />
                    </div>
                    {p.days.map((d) => (
                      <div key={d.date} className="w-[95px]">
                        <DayStrip hours={d.hours} name={p.name} date={d.date} testid={`coverage-strip-${p.user_id}-${d.date}`} />
                      </div>
                    ))}
                  </div>
                ))}
                <div className="mt-2 flex items-center gap-3 border-t border-border pt-2" data-testid="coverage-totals">
                  <div className="w-[190px] shrink-0 text-xs font-semibold text-foreground">People in</div>
                  {(data?.coverage || []).map((c) => (
                    <div key={c.date} className="w-[95px]">
                      <div className="flex gap-[1px]">
                        {HOURS.map((h) => {
                          const v = c.hours[h];
                          return (
                            <span key={h} title={`${dayLabel(c.date)} ${hourLabel(h)} — ${v == null ? "no data" : `${v} in`}`}
                              className={`h-4 w-[3px] rounded-[1px] ${v == null ? "border border-dashed border-border/70" : v === 0 ? "bg-rose-200 dark:bg-rose-900/60" : "bg-primary"}`}
                              style={v ? { opacity: 0.35 + 0.65 * (v / maxCover) } : undefined} />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-1 flex items-center gap-3" data-testid="coverage-daytotals">
                  <div className="w-[190px] shrink-0 text-xs font-semibold text-foreground">Total in-hours <span className="font-normal text-muted-foreground">(avg)</span></div>
                  {dates.map((d, i) => (
                    <div key={d} className="w-[95px] text-center text-[11px]" title={`avg ${(dayTotals[i] / peopleCount).toFixed(1)}h/person`}>
                      <span className="font-bold text-emerald-700 dark:text-emerald-300">{dayTotals[i]}h</span>
                      <span className="ml-1 text-muted-foreground">· {(dayTotals[i] / peopleCount).toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2">
            <Legend />
            {data?.collecting_since && (
              <span className="text-[10px] text-muted-foreground" data-testid="coverage-since">
                Collecting since {new Date(data.collecting_since).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {" · "}{data.event_count} readings
              </span>
            )}
          </div>
          {/* Iter 137 — downtime is downtime: say so, so blank cells aren't
              mistaken for "nobody was in". */}
          {(data?.gap_hours > 0 || (data?.untracked_dates || []).length > 0) && (
            <p className="flex flex-wrap items-center gap-1.5 border-t border-border bg-amber-50 px-4 py-2 text-[11px] text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
              data-testid="coverage-gap-notice">
              <Info className="h-3.5 w-3.5 shrink-0" />
              {data.gap_hours > 0 && (
                <span>
                  No data collected for {data.gap_hours}h in this range
                  {(data.gap_dates || []).length > 0 && ` (${data.gap_dates.map(dayLabel).join(", ")})`}
                  {" — the collector wasn't running, so those hours are gaps, not time off."}
                </span>
              )}
              {(data.untracked_dates || []).length > 0 && (
                <span data-testid="coverage-gap-untracked">
                  {data.untracked_dates.map(dayLabel).join(", ")} {data.untracked_dates.length === 1 ? "is" : "are"} before tracking started.
                </span>
              )}
            </p>
          )}
          {!loading && !people.length && (
            <p className="px-4 pb-3 text-xs text-muted-foreground" data-testid="coverage-empty">No presence history yet.</p>
          )}
        </>
      )}
    </div>
  );
};

/** One person's week — days down, hours across. */
export const PersonWeekDialog = ({ open, onOpenChange, userId, name }) => {
  const [data, setData] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("7");
  const [cStart, setCStart] = useState("");
  const [cEnd, setCEnd] = useState("");

  // Same choices as the Coverage board — "how was yesterday" is asked far more
  // often than "how were the last 7 days".
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const query = (() => {
    const today = new Date();
    if (range === "today") return `start=${ymd(today)}&end=${ymd(today)}`;
    if (range === "yesterday") {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return `start=${ymd(y)}&end=${ymd(y)}`;
    }
    if (range === "custom") return cStart && cEnd ? `start=${cStart}&end=${cEnd}` : null;
    return `days=${range}`;
  })();
  // Iter 122 — the grid itself asks the server for the MDT calendar day.
  const histQuery = range === "today" ? "preset=today"
    : range === "yesterday" ? "preset=yesterday"
      : query;

  useEffect(() => {
    if (!open || !userId || !query) return;
    setLoading(true);
    api.get(`/inout/history?${histQuery}&user_id=${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get(`/presence/conflicts?${query}&user_id=${userId}`)
      .then((r) => setConflicts(r.data))
      .catch(() => setConflicts(null));
  }, [open, userId, query, histQuery]);

  const person = data?.people?.[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="person-week-dialog">
        <DialogHeader>
          <DialogTitle>Presence history — {name}</DialogTitle>
          <DialogDescription>
            Hour-by-hour in/out pattern{data?.tz_abbr ? ` — all times are in ${data.tz_abbr}` : ""}. Sampled every 15 minutes plus every status change.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="person-week-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today" data-testid="person-week-today">Today</SelectItem>
              <SelectItem value="yesterday" data-testid="person-week-yesterday">Yesterday</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="custom" data-testid="person-week-custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
          {range === "custom" && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="date" value={cStart} max={cEnd || undefined} onChange={(e) => setCStart(e.target.value)}
                data-testid="person-week-custom-start" className="rounded-md border border-border bg-background px-2 py-1 text-xs" />
              <span>to</span>
              <input type="date" value={cEnd} min={cStart || undefined} onChange={(e) => setCEnd(e.target.value)}
                data-testid="person-week-custom-end" className="rounded-md border border-border bg-background px-2 py-1 text-xs" />
            </div>
          )}
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          {person && <span className="text-xs text-muted-foreground" data-testid="person-week-total">
            {person.in_hours} hours in over {data.dates?.length || data.days} day{(data.dates?.length || data.days) === 1 ? "" : "s"}
          </span>}
          {conflicts?.total_episodes ? (
            <span className="ml-auto rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300"
              data-testid="person-week-conflicts"
              title="Times Teams said away while Intercom showed them Active — we left their status alone">
              {conflicts.total_episodes} presence conflict{conflicts.total_episodes === 1 ? "" : "s"}
              {conflicts.people?.[0]?.minutes ? ` · ${conflicts.people[0].minutes}m` : ""}
            </span>
          ) : null}
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-max">
            <div className="mb-1 flex gap-[2px] pl-[86px]">
              {HOURS.map((h) => (
                <span key={h} className="w-[14px] text-center text-[8px] text-muted-foreground">
                  {h % 3 === 0 ? hourLabel(h).replace(/(am|pm)/, "") : ""}
                </span>
              ))}
            </div>
            {(person?.days || []).map((d) => (
              <div key={d.date} className="flex items-center gap-[2px] py-[2px]" data-testid={`person-week-row-${d.date}`}>
                <span className="w-[86px] shrink-0 text-[10px] font-medium text-muted-foreground">{dayLabel(d.date)}</span>
                {HOURS.map((h) => {
                  const s = d.hours[h];
                  return (
                    <span key={h} title={`${dayLabel(d.date)} ${hourLabel(h)} — ${s ? LABEL[s] : "no data"}`}
                      className={`h-4 w-[14px] rounded-sm ${s ? CELL[s] : "border border-dashed border-border/70"}`} />
                  );
                })}
              </div>
            ))}
            {!loading && !(person?.days || []).length && (
              <p className="py-6 text-center text-xs text-muted-foreground" data-testid="person-week-empty">No readings yet for this person.</p>
            )}
          </div>
        </div>
        <Legend />
      </DialogContent>
    </Dialog>
  );
};
